import {action, computed, makeObservable, observable, runInAction} from 'mobx';
import Get from 'app/utils/Get';
import {isInjectedRuHost} from 'app/utils/injectedRuHost';
import {FcmConfigInterface} from 'app/interfaces/FcmConfigInterface';
import {type CountryInterface} from 'app/interfaces/CountryInterface';
import {type CountryRestriction} from 'app/utils/countryRestrictions';
import {BuyoutRatesInterface} from 'app/utils/buyout';

interface Config {
    lang: string;
    img_domain: string;
    img_domain_ru?: string;
    mirror_domain: string;
    promo_news_active: number;
    new_year_theme: boolean;
    home_page_casino_filter_id: number | null;
    maintenance: boolean;
    is_ru_host?: boolean;
    buyout?: BuyoutRatesInterface;
}

const DEFAULT_BUYOUT_RATES: BuyoutRatesInterface = {
    enabled: false,
    loss_percent: 12,
    margin_percent: 8
};

export interface ProomConfig {
    playerProfilesToken: string;
    parentOrigin: string;
    userToken: string | null;
    playerProfilesBaseUrl?: string;
}

export interface CupConfig {
    balance: string | null;
    countryCode: string | null;
    currency: string | null;
    email: string | null;
    lang: string;
    nickname: string | null;
    parentOrigin: string;
    timezone: string | null;
    userToken: string | null;
    steamId64: string | null;
}

const DEFAULT_CUP_CONFIG: CupConfig = {
    balance: null,
    countryCode: null,
    currency: null,
    email: null,
    lang: '',
    nickname: null,
    parentOrigin: '',
    steamId64: null,
    timezone: null,
    userToken: null
};
const CUP_CENTRIFUGO_PATH = '/cup-ws';

interface TimeZone {
  id: number;
  name: string;
  description: string;
}

export interface RegFlag {
    code: string;
    name: string;
    image: string;
    restrictions: CountryRestriction[];
}

interface ConfigResponse {
    config?: Config;
    time_zones?: TimeZone[];
    self_exclusion_values?: string[];
    current_locale?: string;
    default_locale?: string;
    amplitude_api_key?: string;
    current_js_ver?: number;
    env?: string;
    users_online?: number;
    show_license?: boolean;
    reg_flags?: string;
    all_countries?: CountryInterface[];
    locales?: string[];
    filter_game_cs?: number;
    filter_game_dota?: number;
    optimal_coef_changing?: string;
    match_formats?: string[];
    mobile_wrapper?: boolean;
    websocket_url?: string;
    fcm_config?: FcmConfigInterface;
    cup_config?: CupConfig;
}

export class ConfigStore {
    @observable loaded = false;

    @observable CONFIG: Config = {} as Config;

    @observable TIME_ZONES: TimeZone[] = [];

    @observable REG_FLAGS: RegFlag[] = [];

    @observable SELF_EXCLUSION_VALUES: string[] = [];

    @observable CURRENT_LOCALE = this.getMetaContent('current-locale') || 'en';

    @observable DEFAULT_LOCALE = this.getMetaContent('default-locale') || 'en';

    @observable AMPLITUDE_API_KEY = '';

    @observable CURRENT_JS_VER = 0;

    @observable ENV = '';

    @observable USERS_ONLINE = 0;

    @observable SHOW_LICENSE = false;

    @observable MOBILE_WRAPPER = false;

    @observable WEBSOCKET_URL: string = '';

    @observable LOCALES: string[] = ['en', 'ru'];

    @observable FILTER_GAME_CS: number = 0;

    @observable FILTER_GAME_DOTA: number = 0;

    @observable OPTIMAL_COEF_CHANGING: string = '';

    @observable MATCH_FORMATS: string[] = [];

    @observable SPORTBOOK_URL: string = '';

    @observable FCM_CONFIG: FcmConfigInterface | null = null;

    @observable ALL_COUNTRIES: CountryInterface[] = [];

    @observable PROOM_CONFIG: ProomConfig = (window as Window & {PROOM_CONFIG?: ProomConfig}).PROOM_CONFIG ||
        {parentOrigin: '', playerProfilesToken: '', userToken: null};

    @observable CUP_CONFIG: CupConfig = DEFAULT_CUP_CONFIG;

    constructor() {
        makeObservable(this);
        this.fetchConfig();
    }

    private getMetaContent(name: string): string | null {
        const meta = document.querySelector(`meta[name="${name}"]`);
        return meta?.getAttribute('content') || null;
    }

    @computed
    get isReady(): boolean {
        return this.loaded;
    }

    @computed
    get isDevelopment(): boolean {
        return this.ENV === 'development';
    }

    @computed
    get MAINTENANCE(): boolean {
        return Boolean(this.CONFIG?.maintenance);
    }

    @computed
    get isRuHost(): boolean {
        return Boolean(this.CONFIG?.is_ru_host) || isInjectedRuHost();
    }

    @computed
    get CUP_LOBBY_URL(): string {
        return this.isRuHost
            ? `https://cup.${window.location.hostname}/lobby/`
            : 'https://cup.egb.net/lobby/';
    }

    @computed
    get CUP_WIDGET_URL(): string {
        return this.isRuHost
            ? `https://cup.${window.location.hostname}/lobby/widget.html`
            : 'https://cup.egb.net/lobby/widget.html';
    }

    @computed
    get CUP_ORIGIN(): string {
        return new URL(this.CUP_LOBBY_URL).origin;
    }

    @computed
    get CUP_CENTRIFUGO_PATH(): string {
        return CUP_CENTRIFUGO_PATH;
    }

    @action
    private updateCoreConfig(json: ConfigResponse): void {
        this.CONFIG = (json.config || {}) as Config;
        this.TIME_ZONES = json.time_zones || [];
        this.SELF_EXCLUSION_VALUES = json.self_exclusion_values || [];
        this.CURRENT_LOCALE = json.current_locale || 'en';
        this.DEFAULT_LOCALE = json.default_locale || 'en';
        this.AMPLITUDE_API_KEY = json.amplitude_api_key || '';
        this.CURRENT_JS_VER = json.current_js_ver || 0;
        this.ENV = json.env || '';
        this.USERS_ONLINE = json.users_online || 0;
        this.FCM_CONFIG = json.fcm_config || null;
    }

    @action
    private updateGameAndUIConfig(json: ConfigResponse): void {
        this.SHOW_LICENSE = Boolean(json.show_license);
        this.REG_FLAGS = this.normalizeFlags(json.reg_flags);
        this.ALL_COUNTRIES = json.all_countries || [];
        this.LOCALES = json.locales || [];
        this.FILTER_GAME_CS = json.filter_game_cs || 0;
        this.FILTER_GAME_DOTA = json.filter_game_dota || 0;
        this.OPTIMAL_COEF_CHANGING = json.optimal_coef_changing || '';
        this.MATCH_FORMATS = json.match_formats || [];
        this.MOBILE_WRAPPER = json.mobile_wrapper || false;
        this.WEBSOCKET_URL = json.websocket_url || '';
        this.CUP_CONFIG = json.cup_config || DEFAULT_CUP_CONFIG;
    }

    @action
    private updateConfigState(json: ConfigResponse): void {
        this.updateCoreConfig(json);
        this.updateGameAndUIConfig(json);
        this.loaded = true;
    }

    @action
    setMaintenance(maintenance: boolean): void {
        this.CONFIG = {...this.CONFIG, maintenance};
    }

    get buyoutConfig(): BuyoutRatesInterface {
        return this.CONFIG?.buyout || DEFAULT_BUYOUT_RATES;
    }

    @action
    setBuyoutSettings(buyout: BuyoutRatesInterface): void {
        this.CONFIG = {...this.CONFIG, buyout};
    }

    @action
    async fetchConfig(): Promise<void> {
        try {
            const locale = this.CURRENT_LOCALE === this.DEFAULT_LOCALE ? '' : this.CURRENT_LOCALE;
            const url = `${locale ? `/${locale}` : ''}/load_config`;
            const response = await new Get({url}).execute();

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            const json = await response.json();

            runInAction(() => {
                this.updateConfigState(json);
            });
        } catch (err) {
            console.error('Failed to fetch config:', err);
            runInAction(() => {
                this.loaded = true;
            });
        }
    }

    normalizeFlags(value: string | undefined): RegFlag[] {
        let parsed: string | RegFlag[] | Record<string, RegFlag> | undefined = value;

        if (typeof parsed === 'string') {
            try {
                parsed = JSON.parse(parsed);
            } catch {
                console.warn('Failed to parse REG_FLAGS JSON string');
                return [];
            }
        }

        if (parsed && !Array.isArray(parsed) && typeof parsed === 'object') {
            parsed = Object.values(parsed);
        }

        if (!Array.isArray(parsed)) {
            return [];
        }

        return parsed
            .filter(item => item && typeof item === 'object')
            .map(item => ({
                code: String(item.code ?? ''),
                image: String(item.image ?? ''),
                name: String(item.name ?? ''),
                restrictions: Array.isArray(item.restrictions) ? item.restrictions : []
            }));
    }
}
